import { z } from "zod";

const OpaqueIdSchema = z.uuid();
const IsoInstantSchema = z.iso.datetime({ offset: true });
export const ChildDeviceCapabilitySchema = z.enum([
  "today:read",
  "tasks:start",
  "tasks:complete",
  "tasks:help",
  "tasks:reorder",
]);

export const SupporterCapabilitySchema = z.enum([
  "tasks:read",
  "tasks:write",
  "tasks:proxy",
  "routines:read",
  "routines:write",
  "evidence:read",
  "evidence:verify",
  "insights:read",
  "reviews:read",
]);

export const MembershipScopeSchema = z
  .object({
    child_id: OpaqueIdSchema.nullable(),
    area_id: OpaqueIdSchema.nullable(),
    capability: SupporterCapabilitySchema,
  })
  .strict()
  .refine((scope) => scope.child_id !== null || scope.area_id !== null, {
    message: "A supporter scope must target a child or Area.",
  });

export const WebAuthnCredentialResponseSchema = z
  .object({
    id: z.string().min(1).max(2048),
    rawId: z.string().min(1).max(2048),
    response: z.record(z.string(), z.unknown()),
    type: z.literal("public-key"),
    clientExtensionResults: z.record(z.string(), z.unknown()),
    authenticatorAttachment: z.enum(["cross-platform", "platform"]).optional(),
  })
  .strict();

export const PasskeyRegistrationOptionsRequestSchema = z
  .object({
    display_name: z.string().trim().min(1).max(120),
    workspace_name: z.string().trim().min(1).max(120),
    timezone: z.string().trim().min(1).max(64),
    device_label: z.string().trim().min(1).max(120),
    bootstrap_code: z.string().min(16).max(512).optional(),
  })
  .strict();

export const PasskeyOptionsResponseSchema = z
  .object({
    ceremony_id: OpaqueIdSchema,
    public_key: z.record(z.string(), z.unknown()),
    expires_at: IsoInstantSchema,
    request_id: z.string().min(1),
  })
  .strict();

export const PasskeyRegistrationVerifyRequestSchema = z
  .object({
    ceremony_id: OpaqueIdSchema,
    credential: WebAuthnCredentialResponseSchema,
  })
  .strict();

export const PasskeyAuthenticationOptionsRequestSchema = z
  .object({
    device_label: z.string().trim().min(1).max(120),
    invitation_token: z.string().min(32).max(512).optional(),
  })
  .strict();

export const PasskeyAuthenticationVerifyRequestSchema = z
  .object({
    ceremony_id: OpaqueIdSchema,
    credential: WebAuthnCredentialResponseSchema,
    device_label: z.string().trim().min(1).max(120),
  })
  .strict();

export const WorkspaceSummarySchema = z
  .object({
    id: OpaqueIdSchema,
    name: z.string().min(1).max(120),
    timezone: z.string().min(1).max(64),
    role: z.enum(["owner", "guardian", "supporter"]).nullable(),
  })
  .strict();

export const WorkspaceSettingsSchema = z
  .object({
    id: OpaqueIdSchema,
    name: z.string().min(1).max(120),
    timezone: z.string().min(1).max(64),
    week_starts_on: z.number().int().min(0).max(6),
    default_cycle_days: z.number().int().min(1).max(31),
    quiet_hours: z
      .object({
        enabled: z.boolean(),
        start: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/u),
        end: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/u),
      })
      .strict(),
    locale: z.string().min(2).max(16),
    version: z.number().int().positive(),
    updated_at: IsoInstantSchema,
  })
  .strict();

export const UpdateWorkspaceSettingsRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    timezone: z.string().trim().min(1).max(64),
    week_starts_on: z.number().int().min(0).max(6),
    default_cycle_days: z.number().int().min(1).max(31),
    quiet_hours: WorkspaceSettingsSchema.shape.quiet_hours,
    locale: z.string().trim().min(2).max(16),
    version: z.number().int().positive(),
  })
  .strict();

export const WorkspaceSettingsResponseSchema = z
  .object({ workspace: WorkspaceSettingsSchema, request_id: z.string().min(1) })
  .strict();

export const AuthenticatedActorSchema = z
  .object({
    id: OpaqueIdSchema,
    type: z.enum(["adult", "child"]),
    workspace_id: OpaqueIdSchema,
    membership_role: z.enum(["owner", "guardian", "supporter"]).nullable(),
    bound_child_id: OpaqueIdSchema.nullable(),
    device_id: OpaqueIdSchema,
    session_id: OpaqueIdSchema,
    capabilities: z.array(ChildDeviceCapabilitySchema).max(5),
    membership_scopes: z.array(MembershipScopeSchema).max(100),
  })
  .strict();

export const BrowserSessionResponseSchema = z
  .object({
    actor: AuthenticatedActorSchema,
    workspace: WorkspaceSummarySchema,
    csrf_token: z.string().min(24).max(256),
    recovery_codes: z.array(z.string().min(10).max(64)).optional(),
    request_id: z.string().min(1),
  })
  .strict();

export const ChildSessionRenewResponseSchema = BrowserSessionResponseSchema.extend({
  absolute_expires_at: z.iso.datetime().nullable(),
  renew_after: z.iso.datetime(),
}).strict();

export const RecoveryLoginRequestSchema = z
  .object({
    recovery_code: z.string().trim().min(10).max(64),
    device_label: z.string().trim().min(1).max(120),
  })
  .strict();

export const ChildEnrollmentCreateRequestSchema = z
  .object({
    child_id: OpaqueIdSchema,
    device_label: z.string().trim().min(1).max(120),
    capabilities: z.array(ChildDeviceCapabilitySchema).min(1).max(5),
    request_id: z.uuid().optional(),
    replaces_enrollment_id: z.uuid().optional(),
  })
  .strict();

export const ChildEnrollmentCreateResponseSchema = z
  .object({
    enrollment_id: OpaqueIdSchema,
    enrollment_url: z.url(),
    expires_at: IsoInstantSchema,
    request_id: z.string().min(1),
  })
  .strict();

export const ChildEnrollmentRedeemRequestSchema = z
  .object({
    token: z.string().min(32).max(512),
  })
  .strict();

export const DeviceSchema = z
  .object({
    id: OpaqueIdSchema,
    actor_type: z.enum(["adult", "child"]),
    child_id: OpaqueIdSchema.nullable(),
    label: z.string().min(1).max(120),
    last_seen_at: IsoInstantSchema.nullable(),
    revoked_at: IsoInstantSchema.nullable(),
    created_at: IsoInstantSchema,
  })
  .strict();

export const DeviceListResponseSchema = z
  .object({
    items: z.array(DeviceSchema),
    request_id: z.string().min(1),
  })
  .strict();

export const DeviceRevokeRequestSchema = z
  .object({
    reason: z.string().trim().min(1).max(240),
  })
  .strict();

export const UpdateDeviceLabelRequestSchema = z
  .object({ label: z.string().trim().min(1).max(120) })
  .strict();

export const PasskeyCredentialSchema = z
  .object({
    id: OpaqueIdSchema,
    device_label: z.string().min(1).max(120),
    transports: z.array(z.string().min(1).max(40)).max(10),
    backed_up: z.boolean(),
    backup_eligible: z.boolean(),
    last_used_at: IsoInstantSchema.nullable(),
    created_at: IsoInstantSchema,
  })
  .strict();

export const PasskeyCredentialListResponseSchema = z
  .object({ items: z.array(PasskeyCredentialSchema), request_id: z.string().min(1) })
  .strict();

export const UpdatePasskeyCredentialRequestSchema = z
  .object({ device_label: z.string().trim().min(1).max(120) })
  .strict();

export const MembershipInvitationScopeSchema = z
  .object({
    child_id: OpaqueIdSchema.nullable(),
    area_id: OpaqueIdSchema.nullable(),
    capability: SupporterCapabilitySchema,
  })
  .strict()
  .refine((scope) => scope.child_id !== null || scope.area_id !== null, {
    message: "A supporter scope must target a child or Area.",
  });

export const CreateMembershipInvitationRequestSchema = z
  .object({
    role: z.enum(["guardian", "supporter"]),
    scopes: z.array(MembershipInvitationScopeSchema).max(100),
    expires_in_hours: z.number().int().min(1).max(168).default(72),
    request_id: z.uuid().optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.role === "guardian" && value.scopes.length !== 0) {
      context.addIssue({
        code: "custom",
        path: ["scopes"],
        message: "Guardian invitations cannot contain supporter scopes.",
      });
    }
    if (value.role === "supporter" && value.scopes.length === 0) {
      context.addIssue({
        code: "custom",
        path: ["scopes"],
        message: "Supporter invitations require at least one scope.",
      });
    }
  });

export const MembershipInvitationSchema = z
  .object({
    id: OpaqueIdSchema,
    role: z.enum(["guardian", "supporter"]),
    status: z.enum(["active", "expired", "revoked", "accepted"]),
    scopes: z.array(MembershipInvitationScopeSchema),
    expires_at: IsoInstantSchema,
    accepted_at: IsoInstantSchema.nullable(),
    created_at: IsoInstantSchema,
  })
  .strict();

export const MembershipInvitationListResponseSchema = z
  .object({ items: z.array(MembershipInvitationSchema), request_id: z.string().min(1) })
  .strict();

export const CreateMembershipInvitationResponseSchema = z
  .object({
    invitation: MembershipInvitationSchema,
    invitation_url: z.url(),
    request_id: z.string().min(1),
  })
  .strict();

export const InspectMembershipInvitationRequestSchema = z
  .object({ token: z.string().min(32).max(512) })
  .strict();

export const MembershipInvitationPublicScopeSchema = z
  .object({
    child_name: z.string().min(1).max(80).nullable(),
    area_name: z.string().min(1).max(120).nullable(),
    capability: SupporterCapabilitySchema,
  })
  .strict();

export const InspectMembershipInvitationResponseSchema = z
  .object({
    workspace_name: z.string().min(1).max(120),
    role: z.enum(["guardian", "supporter"]),
    status: z.enum(["active", "expired", "revoked", "accepted"]),
    scopes: z.array(MembershipInvitationPublicScopeSchema),
    expires_at: IsoInstantSchema,
    request_id: z.string().min(1),
  })
  .strict();

export const InvitationPasskeyRegistrationOptionsRequestSchema = z
  .object({
    token: z.string().min(32).max(512),
    display_name: z.string().trim().min(1).max(120),
    device_label: z.string().trim().min(1).max(120),
  })
  .strict();

export const RevokeMembershipInvitationRequestSchema = z
  .object({ reason: z.string().trim().min(1).max(240) })
  .strict();

export const UpdateWorkspaceMembershipRequestSchema = z
  .object({
    role: z.enum(["guardian", "supporter"]),
    status: z.enum(["active", "suspended"]),
    scopes: z.array(MembershipInvitationScopeSchema).max(100),
    version: z.number().int().positive(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.role === "guardian" && value.scopes.length !== 0) {
      context.addIssue({ code: "custom", path: ["scopes"], message: "Guardians use role access." });
    }
    if (value.role === "supporter" && value.scopes.length === 0) {
      context.addIssue({
        code: "custom",
        path: ["scopes"],
        message: "Supporters require at least one scope.",
      });
    }
  });

export const TransferWorkspaceOwnershipRequestSchema = z
  .object({
    target_membership_id: OpaqueIdSchema,
    expected_actor_version: z.number().int().positive(),
    expected_target_version: z.number().int().positive(),
  })
  .strict();

export const LeaveWorkspaceRequestSchema = z
  .object({ version: z.number().int().positive() })
  .strict();

export const WorkspaceMembershipSchema = z
  .object({
    id: OpaqueIdSchema,
    account_id: OpaqueIdSchema,
    display_name: z.string().min(1).max(120),
    role: z.enum(["owner", "guardian", "supporter"]),
    status: z.enum(["invited", "active", "suspended", "left"]),
    version: z.number().int().positive(),
    accepted_at: IsoInstantSchema.nullable(),
    scopes: z.array(MembershipScopeSchema),
  })
  .strict();

export const WorkspaceMembershipListResponseSchema = z
  .object({ items: z.array(WorkspaceMembershipSchema), request_id: z.string().min(1) })
  .strict();

export const WorkspaceMembershipResponseSchema = z
  .object({ membership: WorkspaceMembershipSchema, request_id: z.string().min(1) })
  .strict();

export const SuccessResponseSchema = z
  .object({
    success: z.literal(true),
    request_id: z.string().min(1),
  })
  .strict();

export type PasskeyRegistrationOptionsRequest = z.infer<
  typeof PasskeyRegistrationOptionsRequestSchema
>;
export type PasskeyRegistrationVerifyRequest = z.infer<
  typeof PasskeyRegistrationVerifyRequestSchema
>;
export type PasskeyAuthenticationVerifyRequest = z.infer<
  typeof PasskeyAuthenticationVerifyRequestSchema
>;
export type ChildEnrollmentCreateRequest = z.infer<typeof ChildEnrollmentCreateRequestSchema>;
export type BrowserSessionResponse = z.infer<typeof BrowserSessionResponseSchema>;
export type MembershipScope = z.infer<typeof MembershipScopeSchema>;
export type SupporterCapability = z.infer<typeof SupporterCapabilitySchema>;
